import { count, plural } from "./format";
import type { Draft, Exception, InboxMessage, VendorPoRow } from "./types";

// What Home shows ("what needs you", D-23), picked from the tools' answers. Pure, so it is tested without a browser.

/**
 * Vendor POs awaiting approval, from vendor_pos: the oldest first (it has waited longest). Only Tare's own POs with a
 * customer PO behind them, since the approval screen opens through the order.
 */
export function approvals(rows: VendorPoRow[]): VendorPoRow[] {
  return rows
    .filter((v) => v.status === "AWAITING_APPROVAL" && v.customerPoId !== null)
    .sort((a, b) => (a.orderedOn ?? "").localeCompare(b.orderedOn ?? "") || a.number.localeCompare(b.number));
}

export interface ApprovedNotSent { vendorPo: VendorPoRow; draftId: number | null }

/**
 * Vendor POs approved but not gone to the supplier, each with its pending email (the next step) or null when none was
 * prepared (then: withdraw the approval and approve again). Longest approved first.
 */
export function approvedNotSent(rows: VendorPoRow[], pendingDrafts: Draft[]): ApprovedNotSent[] {
  return rows
    .filter((v) => v.status === "APPROVED" && !v.sentAt && v.customerPoId !== null)
    .sort((a, b) => (a.approvedAt ?? "").localeCompare(b.approvedAt ?? "") || a.number.localeCompare(b.number))
    .map((v) => ({
      vendorPo: v,
      draftId: pendingDrafts.find((d) => d.status === "PENDING" && d.purpose === "PO_TO_SUPPLIER" && d.vendorPo === v.number)?.draftId ?? null,
    }));
}

export interface DocumentToReview {
  documentId: number;
  filename: string;
  kind: string;
  note: string | null;
  summary: string | null;
  subject: string;
  from: string;
  mailbox: string;
  receivedAt: string;
}

/** Documents the reader wasn't sure of (NEEDS_REVIEW), with the message they came in. */
export function documentsToReview(messages: InboxMessage[]): DocumentToReview[] {
  return messages.flatMap((m) =>
    (m.documents ?? []).filter((d) => d.status === "NEEDS_REVIEW").map((d) => ({
      documentId: d.documentId, filename: d.filename, kind: d.kind, note: d.note ?? null, summary: d.summary ?? null,
      subject: m.subject, from: m.from, mailbox: m.mailbox, receivedAt: m.receivedAt,
    })));
}

const URGENCY = ["CRITICAL", "ELEVATED", "ROUTINE"];

/** Open exceptions, most urgent first, then the soonest thing they block. */
export function byUrgency(rows: Exception[]): Exception[] {
  const rank = (u: string) => (URGENCY.indexOf(u) + 4) % 4;
  return [...rows].sort((a, b) => rank(a.urgency) - rank(b.urgency) || (a.blocksOn ?? "9").localeCompare(b.blocksOn ?? "9"));
}

/** The lede under the date: the findings, with their numbers, in a sentence each. */
export function lede(n: { approvals: number; approvedNotSent?: number; drafts: number; documents: number; exceptions: number; critical: number },
                     canApprove: boolean): string {
  const s: string[] = [];
  if (n.approvals) {
    s.push(`${count(n.approvals)} vendor ${plural(n.approvals, "PO is", "POs are")} waiting for ${canApprove ? "your approval" : "approval"}.`);
  }
  if (n.approvedNotSent) {
    s.push(`${count(n.approvedNotSent)} approved ${plural(n.approvedNotSent, "PO hasn't", "POs haven't")} gone to the supplier yet.`);
  }
  if (n.drafts) s.push(`${count(n.drafts)} ${plural(n.drafts, "email is", "emails are")} drafted for a person to check and send.`);
  if (n.documents) s.push(`${count(n.documents)} ${plural(n.documents, "document", "documents")} from the groups' mail ${plural(n.documents, "needs", "need")} a look.`);
  if (n.exceptions) {
    s.push(`${count(n.exceptions)} ${plural(n.exceptions, "exception is", "exceptions are")} open` +
      (n.critical ? `, ${n.critical} of ${plural(n.exceptions, "it", "them")} critical.` : "."));
  }
  return s.length ? s.join(" ") : "Nothing needs you right now.";
}
